/**
 * Ops simulation Sadha T19 — offline card / UPI payments for reconciliation.
 *
 * A till that is offline takes card or UPI on a manual slip the cashier
 * confirms (Odoo-style). The bill syncs as 'paid offline — unverified'. A
 * MANAGER+ checks the slip or the bank statement and marks it verified here;
 * the POST is audited. Nothing clears the flag automatically (owner decision).
 * Not plan-gated: offline billing is the till path, so every plan reaches it.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Pagination,
  Skeleton,
  EmptyState,
  Button,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import {
  offlinePaymentService,
  type OfflinePayment,
  type OfflinePaymentStatus,
} from '@/services/offline-payment.service';
import { GLASS, cn } from '@/constants/design-tokens';
import { storeTimezone } from '@/lib/store-clock';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';

const PAGE_SIZE = 50;

const TABS: Array<{ value: OfflinePaymentStatus; label: string }> = [
  { value: 'UNVERIFIED', label: 'To verify' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'ALL', label: 'All' },
];

const METHOD_LABELS: Record<string, string> = { CARD: 'Card', UPI: 'UPI', SPLIT: 'Split' };

/** On the bill's store clock (T28), not a fixed IST one. */
function formatDateTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
}

function StatusPill({ row }: { row: OfflinePayment }) {
  return row.payment_verification === 'VERIFIED' ? (
    <span className="rounded-full bg-success-100 px-2 py-0.5 text-xs font-semibold text-success-700 dark:bg-success-900/30 dark:text-success-300">
      Verified
    </span>
  ) : (
    <span className="rounded-full bg-warning-100 px-2 py-0.5 text-xs font-semibold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
      Paid offline — unverified
    </span>
  );
}

function OfflinePaymentsContent() {
  const profile = useAuthStore((s) => s.profile);
  const currency = useTenantCurrency();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? '1');
  const status = (TABS.find((t) => t.value === searchParams.get('status'))?.value ?? 'UNVERIFIED') as OfflinePaymentStatus;

  const [verifying, setVerifying] = useState<OfflinePayment | null>(null);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['bills', 'offline-payments', status, page],
    queryFn: () => offlinePaymentService.list({ status, page, limit: PAGE_SIZE }),
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const handleVerify = async () => {
    if (!verifying) return;
    setIsSaving(true);
    try {
      await offlinePaymentService.verify(verifying.bill_id, note);
      toast({ variant: 'success', title: `Bill ${verifying.bill_number} verified` });
      setVerifying(null);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['bills', 'offline-payments'] });
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not mark it verified',
        description: err instanceof ApiError ? err.message : 'Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="mb-6 rounded-xl bg-gradient-to-r from-brand-600 to-violet-600 p-6 text-white shadow-lg animate-fade-in">
        <h2 className="text-2xl font-bold text-white">Offline card & UPI payments</h2>
        <p className="mt-1 text-brand-50">
          Card and UPI taken while a till was offline. Check each slip or your bank statement, then mark it verified.
        </p>
        {status === 'UNVERIFIED' && (
          <p className="mt-3 inline-flex rounded-full bg-white/20 px-3 py-1 text-sm font-semibold tabular-nums">
            {total.toLocaleString('en-IN')} to verify
          </p>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Verification status">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={status === t.value}
            onClick={() => setSearchParams({ status: t.value })}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200',
              status === t.value
                ? 'bg-gradient-to-r from-brand-600 to-violet-600 text-white shadow-md'
                : 'bg-white text-neutral-600 shadow-sm hover:bg-brand-50 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : error ? (
        <div className={cn(GLASS, 'p-6 text-center text-sm text-danger-600 dark:text-danger-400')}>
          Could not load the offline payments. Please try again.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={status === 'UNVERIFIED' ? 'Nothing to verify' : 'No offline card or UPI payments'}
          description={
            status === 'UNVERIFIED'
              ? 'Every card and UPI payment taken offline has been checked.'
              : 'Card and UPI taken while a till was offline show here.'
          }
          variant="page"
        />
      ) : (
        <div className={cn(GLASS, 'overflow-x-auto shadow-md')}>
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-brand-50 to-violet-50 text-left text-xs font-semibold uppercase text-neutral-600 dark:from-brand-900/20 dark:to-violet-900/10 dark:text-neutral-300">
              <tr>
                <th className="px-4 py-3">Bill</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3 text-right">To check</th>
                <th className="px-4 py-3 text-right">Bill total</th>
                <th className="px-4 py-3">Slip ref</th>
                <th className="px-4 py-3">Store</th>
                <th className="px-4 py-3">Cashier</th>
                <th className="px-4 py-3">Sold</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {rows.map((p) => (
                <tr
                  key={p.bill_id}
                  data-testid={`offline-payment-${p.bill_id}`}
                  className="transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 animate-slide-up dark:even:bg-neutral-800/40 dark:hover:bg-brand-900/20"
                >
                  <td className="px-4 py-3">
                    <Link to={`/bills/${p.bill_id}`} className="font-medium text-brand-600 hover:underline dark:text-brand-400">
                      {p.bill_number}
                    </Link>
                    <p className="font-mono text-[11px] text-neutral-500 dark:text-neutral-400">
                      Offline ref {p.offline_bill_number}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-neutral-700 dark:text-neutral-200">
                    {METHOD_LABELS[p.payment_method] ?? p.payment_method}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-neutral-900 dark:text-white">
                    {formatPaise(p.unverified_amount, currency)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-600 dark:text-neutral-300">
                    {formatPaise(p.grand_total, currency)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-700 dark:text-neutral-200">
                    {p.payment_reference ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-neutral-700 dark:text-neutral-200">{p.location_name ?? '—'}</td>
                  <td className="px-4 py-3 text-neutral-700 dark:text-neutral-200">{p.cashier_name ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums text-neutral-500 dark:text-neutral-400">
                    {formatDateTime(p.sold_at, storeTimezone(profile, p.location_id))}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill row={p} />
                    {p.payment_verification === 'VERIFIED' && p.verified_by_name && (
                      <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">by {p.verified_by_name}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.payment_verification === 'UNVERIFIED' && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          setNote('');
                          setVerifying(p);
                        }}
                        className="bg-gradient-to-r from-teal-500 to-success-600 shadow-md hover:shadow-lg transition-all duration-200"
                      >
                        Mark verified
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={data?.meta?.totalPages ?? 1}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setSearchParams({ status, page: String(p) })}
          />
        </div>
      )}

      <ConfirmDialog
        open={verifying !== null}
        title={`Mark ${verifying?.bill_number ?? 'this bill'} verified?`}
        description={
          verifying
            ? `You checked the ${METHOD_LABELS[verifying.payment_method] ?? verifying.payment_method} slip or bank statement for ${formatPaise(verifying.unverified_amount, currency)}. This is recorded in the audit log.`
            : ''
        }
        confirmLabel="Mark verified"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          void handleVerify();
        }}
        onCancel={() => setVerifying(null)}
        loading={isSaving}
      >
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          aria-label="Verification note"
          placeholder="What you checked (optional)"
          className="mt-3 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100"
        />
      </ConfirmDialog>
    </>
  );
}

export function OfflinePaymentsPage() {
  return (
    <PageContainer
      title="Offline Payments"
      subtitle="Card and UPI taken while a till was offline"
      breadcrumbs={[{ label: 'Sync Health', href: '/sync-health' }, { label: 'Offline payments' }]}
    >
      <ErrorBoundary>
        <OfflinePaymentsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
